// Applying to sell on Aldoo, and waiting to hear back.
//
// One page for the whole of it: a company that has not applied sees the form,
// one that has sees where it stands, and one that was turned down sees why —
// with the form again underneath, because being turned down is not the end of
// the conversation. Once a company is approved this page is gone from the
// menu; there is nothing left to say.
import React, { useEffect, useState } from "react"
import { CheckCircleIcon, ClockIcon, XCircleIcon } from "@heroicons/react/24/solid"
import { PhotoIcon, XMarkIcon, EnvelopeOpenIcon, DocumentMagnifyingGlassIcon, BuildingStorefrontIcon, ReceiptPercentIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import AddMediaDialog from "aldoo-ra/AddMediaDialog"
import { showNotification } from "aldoo-ra/Notification"
import { ApplyToSell, UploadApplicationPicture, VendorStatus } from "../AdminShared/api"
import { salesRef } from "./salesRef"
import { Empty, errorMessage } from "../AdminShared/ui"
import useT from "../Storefront/hooks/useT"
import { companyNameError, emailError, phoneError } from "aldoo-ra/Store/contact-validation"
import { personNameError } from "aldoo-ra/Store/address-validation"

export const MAX_PICTURES = 5
// what the API takes of "what do you sell" (vendor-submissions {apply})
const MAX_DESCRIPTION = 5000

// what each field says when it is wrong
export const fieldMessages = (t) => ({
  company: t`Enter the company name: 2–120 characters, with letters, no < or >`,
  contactName: t`Enter your first and last name`,
  email: t`Enter a valid email address, e.g. name@example.com`,
  phone: t`Enter a valid phone number, e.g. +359 88 123 4567`,
  description: t`Tell us what you sell (up to 5000 characters)`,
  website: t`Enter a link that starts with http:// or https://, or leave it empty`,
})

/** Whether a link is one the API keeps (vendor-submissions {apply}): empty, or a web address. */
const websiteError = (value) => {
  const trimmed = (value || "").trim()
  if (!trimmed) return false
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`
  if (withScheme.length > 500) return true
  try {
    const url = new URL(withScheme)
    return !["http:", "https:"].includes(url.protocol) || !url.hostname.includes(".")
  } catch {
    return true
  }
}

/**
 * { field: message } for what the application cannot be sent with: the rules
 * vendor-submissions {apply} applies (aldoo-ra/Store/contact-validation). Also the admin's Register vendor
 * (AdminVendors/RegisterVendorDialog), which sends the same application for a company.
 */
export const applicationErrors = (form, t) => {
  const messages = fieldMessages(t)
  const errors = {}
  if (companyNameError(form.company)) errors.company = messages.company
  if (personNameError(form.contactName)) errors.contactName = messages.contactName
  if (emailError(form.email)) errors.email = messages.email
  if (phoneError(form.phone)) errors.phone = messages.phone
  if (!form.description.trim() || form.description.length > MAX_DESCRIPTION) errors.description = messages.description
  if (websiteError(form.website)) errors.website = messages.website
  return errors
}

/** How long a company has been at this. */
export const EXPERIENCE = [
  { value: "starting", label: "Just starting" },
  { value: "1", label: "About a year" },
  { value: "3", label: "A few years" },
  { value: "5", label: "Five years or more" },
  { value: "10", label: "Ten years or more" },
]

const StatusCard = ({ icon: Icon, tone, title, children, testId }) => (
  <div className={`flex gap-4 p-6 rounded-2xl border ${tone}`} data-testid={testId}>
    <Icon className="w-9 h-9 shrink-0" />
    <div className="min-w-0">
      <h3 className="m-0 mb-1 text-base md:text-lg font-bold">
        {title}
      </h3>
      <Typography variant="txt_dim" className="leading-relaxed">
        {children}
      </Typography>
    </div>
  </div>
)

// The form in the merchant landing's look (MerchantLanding.jsx): its
// typography, its rounded cards and its colours, not the admin's. The
// fields' look is the "registration" Input variant (Variants/Input.jsx).

/** A labelled field: the label (for the control with `id`), the control, then what is wrong or a hint. */
const FormField = ({ id, label, hint, error, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Typography variant="txt1_accent" component="label" htmlFor={id}>
      {label}
    </Typography>
    {children}
    {error ? (
      <Typography variant="txt2" role="alert" className="text-error">
        {error}
      </Typography>
    ) : (
      hint && (
        <Typography variant="txt2_dim" className="leading-snug">
          {hint}
        </Typography>
      )
    )}
  </div>
)

/** One part of the form: a numbered card with its title and what it is for. */
const FormSection = ({ number, title, subtitle, children, testId }) => (
  <section className="rounded-3xl border border-bordered bg-paper p-5 md:p-8 shadow-[0_10px_30px_rgba(0,0,0,0.04)]" data-testid={testId}>
    <div className="flex items-start gap-4 mb-6">
      <span className="w-10 h-10 shrink-0 rounded-xl bg-primary text-white flex items-center justify-center text-[15px] font-bold tabular-nums">{number}</span>
      <div className="min-w-0">
        <h2 className="m-0 text-[20px] md:text-[22px] font-bold leading-tight text-text">{title}</h2>
        {subtitle && <p className="m-0 mt-1 text-[14px] md:text-[15px] leading-relaxed text-text_secondary">{subtitle}</p>}
      </div>
    </div>
    <div className="flex flex-col gap-5">{children}</div>
  </section>
)

export default function Registration({ org, onChanged, fresh = false }) {
  const t = useT()
  // `fresh`: an application for one more company — nothing to look up, the
  // form from the start (the status of the applicant's other companies is
  // not this one's)
  const [status, setStatus] = useState(fresh ? {} : null)
  const [form, setForm] = useState({ company: org?.name || "", contactName: "", email: org?.email || "", phone: org?.phone || "", description: "", experience: "starting", website: "" })
  const [pictures, setPictures] = useState([])
  const [busy, setBusy] = useState("")
  const [errors, setErrors] = useState({})
  // the framework's media dialog: drop a file on it, paste one, or give it a
  // url, then crop before it is sent
  const [picking, setPicking] = useState(false)
  // a file dropped on the picture strip itself opens the dialog with it, so
  // dragging into the form works without finding the button first
  const [dropped, setDropped] = useState(null)

  const load = () => VendorStatus(org?._id).then((res) => setStatus(errorMessage(res) ? { error: errorMessage(res) } : res))
  useEffect(() => {
    if (fresh) return
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org?._id])

  useEffect(() => {
    setForm((current) => ({ ...current, company: current.company || org?.name || "", email: current.email || org?.email || "", phone: current.phone || org?.phone || "" }))
  }, [org?._id])

  const set = (patch) => setForm((current) => ({ ...current, ...patch }))
  // a field that was marked wrong is checked again when it is left
  const recheck = (key) => () => {
    if (!errors[key]) return
    const found = applicationErrors(form, t)
    setErrors((current) => {
      const next = { ...current }
      if (found[key]) next[key] = found[key]
      else delete next[key]
      return next
    })
  }

  const room = MAX_PICTURES - pictures.length

  /** What the dialog hands over when the picture is cropped and ready. */
  const uploadPicture = async (file) => {
    setBusy("upload")
    try {
      const res = await UploadApplicationPicture(file)
      const failed = errorMessage(res)
      if (failed) return { error: failed }
      return res
    } finally {
      setBusy("")
    }
  }

  const openPicker = (file = null) => {
    if (room <= 0) return showNotification({ message: t`Five pictures is the most we ask for`, className: "bg-admin_error text-white" })
    setDropped(file ? URL.createObjectURL(file) : null)
    setPicking(true)
  }

  const closePicker = () => {
    setPicking(false)
    if (dropped) URL.revokeObjectURL(dropped)
    setDropped(null)
  }

  const apply = async () => {
    const found = applicationErrors(form, t)
    setErrors(found)
    if (Object.keys(found).length) {
      return showNotification({ message: t`Tell us your name, the company, an email, a phone and what you sell`, className: "bg-admin_error text-white" })
    }
    setBusy("apply")
    try {
      // the sales agent whose link brought them, when one did
      const res = await ApplyToSell({ ...form, screenshots: pictures, orgId: org?._id, salesRef: salesRef() })
      // a field the API refused is marked on the form, in the form's words
      if (res?.error === "invalid_field" && res.field) {
        const message = fieldMessages(t)[res.field] || res.message
        setErrors({ [res.field]: message })
        return showNotification({ message, className: "bg-admin_error text-white" })
      }
      const failed = errorMessage(res)
      if (failed) return showNotification({ message: failed, className: "bg-admin_error text-white" })
      showNotification({ message: t`Your application is with us`, className: "bg-admin_success text-white" })
      if (fresh) setStatus({ verification: "pending" })
      else await load()
      onChanged?.(res)
      // the answer is at the top of the page (the pending card), not where the
      // button was
      window.scrollTo({ top: 0, behavior: "smooth" })
    } finally {
      setBusy("")
    }
  }

  if (!status) return <Empty>{t`Loading…`}</Empty>

  const state = status.verification || status.application?.status || null
  const pending = state === "pending"
  const rejected = state === "rejected"
  const approved = state === "approved"

  return (
    <div className="flex flex-col gap-5" data-testid="vendor-registration">
      {pending && (
        <StatusCard icon={ClockIcon} tone="border-warning/40 bg-warning_bg text-warning" title={t`Your company is pending approval`} testId="registration-pending">
          {t`We are reading your application. You can fill in your company details in the meantime — selling opens once we have said yes.`}
        </StatusCard>
      )}
      {approved && (
        <StatusCard icon={CheckCircleIcon} tone="border-success/40 bg-success_bg text-success" title={t`You are a verified merchant`} testId="registration-approved">
          {status.notes || t`Everything is open to you: products, orders and sales.`}
        </StatusCard>
      )}
      {rejected && (
        <StatusCard icon={XCircleIcon} tone="border-error/40 bg-error/5 text-error" title={t`Your application was not approved`} testId="registration-rejected">
          {status.notes ? status.notes : t`No reason was given. You may apply again with more about what you sell.`}
        </StatusCard>
      )}

      {!pending && !approved && (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 lg:gap-8 items-start" data-testid="apply-form">
          <div className="flex flex-col gap-5 min-w-0">
            {rejected && <h2 className="m-0 text-[24px] md:text-[28px] font-bold leading-tight">{t`Apply again`}</h2>}

            <FormSection number={1} title={t`About your company`} subtitle={t`Who you are and where we answer`} testId="apply-section-company">
              <div className="grid md:grid-cols-2 gap-5">
                <FormField id="apply-contact-name" label={t`Your name`} hint={t`First and last name — whom we talk to`} error={errors.contactName}>
                  <Input
                    variant="registration"
                    id="apply-contact-name"
                    value={form.contactName}
                    onChange={(event) => set({ contactName: event.target.value })}
                    onBlur={recheck("contactName")}
                    autoComplete="name"
                    maxLength={100}
                    aria-invalid={errors.contactName ? true : undefined}
                    data-testid="apply-contact-name"
                  />
                </FormField>
                <FormField id="apply-company" label={t`Company name`} error={errors.company}>
                  <Input
                    variant="registration"
                    id="apply-company"
                    value={form.company}
                    onChange={(event) => set({ company: event.target.value })}
                    onBlur={recheck("company")}
                    maxLength={120}
                    aria-invalid={errors.company ? true : undefined}
                    data-testid="apply-company"
                  />
                </FormField>
                <FormField id="apply-email" label={t`Email`} error={errors.email}>
                  <Input
                    variant="registration"
                    id="apply-email"
                    value={form.email}
                    onChange={(event) => set({ email: event.target.value })}
                    onBlur={recheck("email")}
                    type="email"
                    maxLength={254}
                    aria-invalid={errors.email ? true : undefined}
                    data-testid="apply-email"
                  />
                </FormField>
                <FormField id="apply-phone" label={t`Phone`} error={errors.phone}>
                  <Input
                    variant="registration"
                    id="apply-phone"
                    value={form.phone}
                    onChange={(event) => set({ phone: event.target.value })}
                    onBlur={recheck("phone")}
                    type="tel"
                    autoComplete="tel"
                    maxLength={20}
                    placeholder="+359 88 123 4567"
                    aria-invalid={errors.phone ? true : undefined}
                    data-testid="apply-phone"
                  />
                </FormField>
              </div>
              {/* how long they have been at it: one tap, the options in sight */}
              <div className="flex flex-col gap-2">
                <span className="text-[14px] font-semibold text-text">{t`Years in your field`}</span>
                <div className="flex flex-wrap gap-2" role="radiogroup" data-testid="apply-experience">
                  {EXPERIENCE.map((option) => {
                    const on = form.experience === option.value
                    return (
                      <button
                        key={option.value}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => set({ experience: option.value })}
                        className={`h-10 px-4 rounded-full border text-[14px] font-semibold transition-colors ${on ? "bg-primary border-primary text-white" : "bg-paper border-bordered text-text hover:border-primary"}`}
                      >
                        {t(option.label)}
                      </button>
                    )
                  })}
                </div>
              </div>
            </FormSection>

            <FormSection number={2} title={t`What you sell`} subtitle={t`What you offer and who buys it`} testId="apply-section-products">
              <FormField id="apply-description" label={t`What do you sell?`} hint={t`Books, music, toys, licences — and who buys them`} error={errors.description}>
                <Input
                  multiline
                  variant="registration"
                  id="apply-description"
                  value={form.description}
                  onChange={(event) => set({ description: event.target.value })}
                  onBlur={recheck("description")}
                  maxLength={MAX_DESCRIPTION}
                  aria-invalid={errors.description ? true : undefined}
                  rows={6}
                  data-testid="apply-description"
                />
              </FormField>
              <FormField
                id="apply-website"
                label={t`Website or catalogue (optional)`}
                hint={t`A link to your shop, a product page or a catalogue. No site yet? Leave it empty.`}
                error={errors.website}
              >
                <Input
                  variant="registration"
                  id="apply-website"
                  value={form.website}
                  onChange={(event) => set({ website: event.target.value })}
                  onBlur={recheck("website")}
                  type="url"
                  inputMode="url"
                  maxLength={500}
                  placeholder="https://"
                  aria-invalid={errors.website ? true : undefined}
                  data-testid="apply-website"
                />
              </FormField>
            </FormSection>

            <FormSection number={3} title={t`Pictures (optional)`} subtitle={t`Up to five pictures of your products — they help, but you can apply without them`} testId="apply-section-pictures">
              <div
                className="flex flex-wrap items-center gap-3 p-3 -m-3 rounded-2xl border-2 border-dashed border-transparent transition-colors data-[over=true]:border-primary data-[over=true]:bg-primary/5"
                data-testid="apply-pictures"
                onDragOver={(event) => {
                  event.preventDefault()
                  event.currentTarget.dataset.over = "true"
                }}
                onDragLeave={(event) => {
                  event.currentTarget.dataset.over = "false"
                }}
                onDrop={(event) => {
                  event.preventDefault()
                  event.currentTarget.dataset.over = "false"
                  const file = [...(event.dataTransfer?.files || [])].find((one) => one.type.startsWith("image/"))
                  if (file) openPicker(file)
                }}
              >
                {pictures.map((url) => (
                  <span key={url} className="relative w-[96px] h-[96px] rounded-xl overflow-hidden border border-bordered bg-neutral">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setPictures((current) => current.filter((one) => one !== url))}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white inline-flex items-center justify-center"
                      aria-label={t`Remove`}
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </span>
                ))}
                {room > 0 && (
                  <button
                    type="button"
                    onClick={() => openPicker()}
                    disabled={busy === "upload"}
                    className="w-[96px] h-[96px] rounded-xl border-2 border-dashed border-bordered text-text_secondary hover:border-primary hover:text-primary flex flex-col items-center justify-center gap-1 transition-colors"
                    data-testid="apply-add-picture"
                  >
                    <PhotoIcon className="w-7 h-7" />
                    <span className="text-[12px] font-semibold">{busy === "upload" ? t`Uploading…` : t`Add`}</span>
                  </button>
                )}
              </div>
              <span className="text-[13px] text-text_secondary">{t`Drop a picture here, paste one, or give a link.`}</span>
            </FormSection>

            {picking && (
              <AddMediaDialog
                imagesOnly
                currentImageUrl={dropped}
                uploadFn={uploadPicture}
                onUse={(result) => {
                  closePicker()
                  if (result?.url) setPictures((current) => [...current, result.url].slice(0, MAX_PICTURES))
                }}
                onCancel={closePicker}
              />
            )}

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
              <Button 
                variant="primary_l" 
                onClick={apply} 
                disabled={!!busy} 
                data-testid="apply-submit"
              >
                {busy === "apply" ? t`Sending…` : t`Send application`}
              </Button>

              <Typography variant="txt1_dim">
                {t`Somebody reads every application — you will hear back by email.`}
              </Typography>
            </div>
          </div>

          {/* what happens once it is sent */}
          <aside className="lg:sticky lg:top-24 rounded-3xl bg-dark text-white p-6 md:p-7" data-testid="apply-next">
            <span className="inline-block mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-primary">
              {t`What happens next`}
            </span>
            <ol className="m-0 p-0 list-none flex flex-col gap-5">
              {[
                [DocumentMagnifyingGlassIcon, t`We read your application`, t`Somebody reads every one, usually within a few days.`],
                [EnvelopeOpenIcon, t`We answer by email`, t`With the commission rate for your business category, for your contract.`],
                [BuildingStorefrontIcon, t`You set up your shop`, t`Add your products or have us import your catalogue, and start selling.`],
              ].map(([Icon, title, body]) => (
                <li key={title} className="flex gap-3">
                  <span className="w-10 h-10 shrink-0 rounded-xl bg-paper/10 text-primary flex items-center justify-center">
                    <Icon className="w-5 h-5" />
                  </span>
                  <span className="min-w-0">
                    <Typography 
                      variant="txt_accent" 
                      className="text-text_inv leading-snug"
                    >
                      {title}
                    </Typography>
                    <Typography variant="txt1" className="block leading-relaxed text-text_inv/70 mt-0.5">
                      {body}
                    </Typography>
                  </span>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex items-start gap-3 p-3 rounded-xl bg-paper/10">
              <ReceiptPercentIcon className="w-6 h-6 shrink-0 text-primary" />
              <Typography 
                variant="txt1" 
                className="leading-snug text-text_inv/80"
              >
                {t`Free to apply — you pay only the commission on what sells.`}
              </Typography>
            </div>
          </aside>
        </div>
      )}
    </div>
  )
}
